/**
 * 规则集合
 * 包含所有内置的验证规则
 */

// 导入基础规则
import { 
  componentNamingRule, 
  textContentRule, 
  layerDepthRule, 
  elementSizeRule, 
  accessibilityRule 
} from './basic-rules.js';

// 导入数据提取规则
import { 
  dataExtractionValidationRule,
  layoutExtractor,
  textExtractor,
  visualsExtractor,
  componentExtractor,
  allExtractors,
  layoutAndText,
  contentOnly,
  visualsOnly,
  layoutOnly
} from './data-extraction.js';

// 导入数据预处理规则
import { 
  FigmaDataPreprocessor,
  FigmaDataCompressor,
  dataPreprocessingRule,
  dataCompressionRule
} from './data-preprocessing.js';

// 导入布局分析规则
import { 
  LayoutAnalyzer,
  layoutAnalysisRule,
  autoLayoutOptimizationRule
} from './layout-analysis.js';

import type { Rule } from '../types/index.js';

/**
 * 所有内置规则的集合
 */
export const builtInRules: Rule[] = [
  // 基础验证规则
  componentNamingRule,
  textContentRule,
  layerDepthRule,
  elementSizeRule,
  accessibilityRule,
  
  // 数据提取规则
  dataExtractionValidationRule,
  
  // 数据预处理规则
  dataPreprocessingRule,
  dataCompressionRule,
  
  // 布局分析规则
  layoutAnalysisRule,
  autoLayoutOptimizationRule
];

/**
 * 按类别分组的规则
 */
export const rulesByCategory = {
  // 基础规则
  structure: [
    componentNamingRule,
    layerDepthRule,
    dataPreprocessingRule,
    dataCompressionRule
  ],
  
  // 内容规则
  content: [
    textContentRule
  ],
  
  // 设计规则
  design: [
    elementSizeRule
  ],
  
  // 可访问性规则
  accessibility: [
    accessibilityRule
  ],
  
  // 数据提取规则
  extraction: [
    dataExtractionValidationRule
  ],
  
  // 布局规则
  layout: [
    layoutAnalysisRule,
    autoLayoutOptimizationRule
  ]
};

/**
 * 按严重程度分组的规则
 */
export const rulesBySeverity = {
  error: builtInRules.filter(rule => rule.severity === 'error'),
  warning: builtInRules.filter(rule => rule.severity === 'warning'),
  info: builtInRules.filter(rule => rule.severity === 'info')
};

/**
 * 数据提取器集合
 */
export const extractors = {
  // 单个提取器
  layoutExtractor,
  textExtractor,
  visualsExtractor,
  componentExtractor,
  
  // 提取器组合
  allExtractors,
  layoutAndText,
  contentOnly,
  visualsOnly,
  layoutOnly
};

/**
 * 工具类集合
 */
export const processors = {
  FigmaDataPreprocessor,
  FigmaDataCompressor,
  LayoutAnalyzer
};

/**
 * 默认规则配置
 */
export const defaultRuleConfig = {
  // 基础规则 - 默认启用
  'component-naming': { enabled: true, severity: 'warning' },
  'text-content': { enabled: true, severity: 'info' },
  'layer-depth': { enabled: true, severity: 'warning' },
  'element-size': { enabled: true, severity: 'warning' },
  'accessibility': { enabled: true, severity: 'error' },
  
  // 数据提取规则 - 默认启用
  'data-extraction-validation': { enabled: true, severity: 'error' },
  
  // 数据预处理规则 - 默认启用
  'data-preprocessing': { enabled: true, severity: 'warning' },
  'data-compression': { enabled: true, severity: 'info' },
  
  // 布局规则 - 默认启用
  'layout-analysis': { enabled: true, severity: 'warning' },
  'auto-layout-optimization': { enabled: true, severity: 'info' }
};

/**
 * 推荐的规则集合配置
 */
export const recommendedRuleSets = {
  // 基础验证 - 适用于所有项目
  basic: [
    'component-naming',
    'text-content',
    'layer-depth',
    'element-size'
  ],
  
  // 严格验证 - 适用于生产环境
  strict: [
    'component-naming',
    'text-content', 
    'layer-depth',
    'element-size',
    'accessibility',
    'data-extraction-validation',
    'layout-analysis'
  ],
  
  // 完整验证 - 包含所有规则
  complete: builtInRules.map(rule => rule.name),
  
  // 性能优化 - 专注于性能相关规则
  performance: [
    'data-preprocessing',
    'data-compression',
    'layout-analysis'
  ],
  
  // 数据提取 - 专注于数据提取相关规则
  extraction: [
    'data-extraction-validation',
    'data-preprocessing',
    'data-compression'
  ],
  
  // 布局优化 - 专注于布局相关规则
  layout: [
    'layout-analysis',
    'auto-layout-optimization'
  ]
};

/**
 * 获取指定类别的规则
 */
export function getRulesByCategory(category: keyof typeof rulesByCategory): Rule[] {
  return rulesByCategory[category] || [];
}

/**
 * 获取指定严重程度的规则
 */
export function getRulesBySeverity(severity: keyof typeof rulesBySeverity): Rule[] {
  return rulesBySeverity[severity] || [];
}

/**
 * 根据规则名称获取规则
 */
export function getRuleByName(name: string): Rule | undefined {
  return builtInRules.find(rule => rule.name === name);
}

/**
 * 获取推荐的规则集合
 */
export function getRecommendedRuleSet(setName: keyof typeof recommendedRuleSets): Rule[] {
  const ruleNames = recommendedRuleSets[setName] || [];
  return ruleNames.map(name => getRuleByName(name)).filter(Boolean) as Rule[];
}

/**
 * 创建自定义规则配置
 */
export function createCustomRuleConfig(
  enabledRules: string[],
  customSeverity?: Record<string, 'error' | 'warning' | 'info'>
): Record<string, any> {
  const config: Record<string, any> = {};
  
  // 默认禁用所有规则
  builtInRules.forEach(rule => {
    config[rule.name] = { enabled: false };
  });
  
  // 启用指定规则
  enabledRules.forEach(ruleName => {
    if (config[ruleName]) {
      config[ruleName].enabled = true;
      
      // 应用自定义严重程度
      if (customSeverity && customSeverity[ruleName]) {
        config[ruleName].severity = customSeverity[ruleName];
      }
    }
  });
  
  return config;
}

// 导出所有规则和工具
export * from './basic-rules.js';
export * from './data-extraction.js';
export * from './data-preprocessing.js';
export * from './layout-analysis.js';

// 默认导出
export default {
  builtInRules,
  rulesByCategory,
  rulesBySeverity,
  extractors,
  processors,
  defaultRuleConfig,
  recommendedRuleSets,
  getRulesByCategory,
  getRulesBySeverity,
  getRuleByName,
  getRecommendedRuleSet,
  createCustomRuleConfig
};